/**
 * User Invites Page
 * Page for regular users to manage their invite codes
 */
import React, { useState, useEffect } from 'react';
import { Box, Flex, Button, Text, useToast, Spinner, VStack, HStack, Select } from '@chakra-ui/react';
import { Plus } from 'lucide-react';
import { inviteService, type Invite } from '../services/InviteService';
import { useAuth } from '../contexts/AuthContext';
import InviteList from '../components/invites/InviteList';
import GenerateInviteModal from '../components/invites/GenerateInviteModal';

export const InvitesPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const toast = useToast();

  // State
  const [invites, setInvites] = useState<Invite[]>([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 25,
    pages: 1,
  });
  const [availableCount, setAvailableCount] = useState(0);

  // Filters
  const [statusFilter, setStatusFilter] = useState<'all' | 'available' | 'used'>('all');

  // Modal
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);

  // Load invites
  const loadInvites = async (params?: { page?: number; status?: string }) => {
    try {
      setLoading(true);
      const response = await inviteService.getMyInvites(
        statusFilter,
        pagination.page,
        pagination.limit
      );

      setInvites(response.invites);
      setPagination(response.pagination);
      setAvailableCount(response.available_count);
    } catch (error: any) {
      toast({
        title: 'Error loading invites',
        description: error.response?.data?.detail || 'Failed to load invites',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  // Initial load
  useEffect(() => {
    if (isAuthenticated) {
      loadInvites();
    }
  }, [isAuthenticated]);

  // Apply filter when it changes
  useEffect(() => {
    if (isAuthenticated) {
      loadInvites({ page: 1 });
    }
  }, [statusFilter]);

  // Handle pagination
  const handlePageChange = (newPage: number) => {
    setPagination({ ...pagination, page: newPage });
    loadInvites({ page: newPage });
  };

  // Handle generate success
  const handleGenerateSuccess = () => {
    setIsGenerateModalOpen(false);
    loadInvites({ page: 1 });
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return (
      <Box p={8} textAlign="center">
        <Text fontSize="lg">Please log in to access this page</Text>
      </Box>
    );
  }

  return (
    <VStack spacing={3} align="stretch">
      {/* Header */}
      <Flex justify="space-between" align="center" mb={2}>
        <Button
          leftIcon={<Plus size={14} />}
          onClick={() => setIsGenerateModalOpen(true)}
          size="xs"
          colorScheme="blue"
          isDisabled={user?.invites_available === 0 && user?.role !== 'admin'}
        >
          Generate
        </Button>
      </Flex>

      {/* Stats - Compact Grid */}
      <HStack spacing={2}>
        <Box bg="white" p={2} borderRadius="md" shadow="sm" flex={1}>
          <Text fontSize="xs" color="gray.600">
            Available
          </Text>
          <Text fontSize="xl" fontWeight="bold">
            {user?.role === 'admin' ? '∞' : user?.invites_available || 0}
          </Text>
        </Box>
        <Box bg="white" p={2} borderRadius="md" shadow="sm" flex={1}>
          <Text fontSize="xs" color="gray.600">
            Total
          </Text>
          <Text fontSize="xl" fontWeight="bold">
            {pagination.total}
          </Text>
        </Box>
        <Box bg="white" p={2} borderRadius="md" shadow="sm" flex={1}>
          <Text fontSize="xs" color="gray.600">
            Active
          </Text>
          <Text fontSize="xl" fontWeight="bold" color="blue.500">
            {invites.filter((i) => i.is_active && !i.used_at).length}
          </Text>
        </Box>
        <Box bg="white" p={2} borderRadius="md" shadow="sm" flex={1}>
          <Text fontSize="xs" color="gray.600">
            Used
          </Text>
          <Text fontSize="xl" fontWeight="bold" color="green.500">
            {invites.filter((i) => i.used_at).length}
          </Text>
        </Box>
      </HStack>

      {/* Filter */}
      <Box bg="white" p={2} borderRadius="md" shadow="sm">
        <HStack spacing={2}>
          <Text fontSize="xs" fontWeight="medium" color="gray.600">
            Filter:
          </Text>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as any)}
            maxW="150px"
            size="xs"
          >
            <option value="all">All</option>
            <option value="available">Available</option>
            <option value="used">Used</option>
          </Select>
        </HStack>
      </Box>

      {/* Invites List */}
      <Box bg="white" borderRadius="md" shadow="sm" overflow="hidden">
        {loading ? (
          <Flex justify="center" align="center" py={10}>
            <Spinner size="md" color="blue.500" />
          </Flex>
        ) : invites.length === 0 ? (
          <Flex justify="center" align="center" py={10} direction="column">
            <Text fontSize="sm" color="gray.500" mb={1}>
              No invites yet
            </Text>
            {user?.invites_available && user.invites_available > 0 && (
              <Text fontSize="xs" color="gray.400">
                Click "Generate" to create your first invite
              </Text>
            )}
          </Flex>
        ) : (
          <>
            <InviteList invites={invites} />

            {/* Pagination */}
            {pagination.pages > 1 && (
              <Flex justify="space-between" align="center" p={2} borderTop="1px" borderColor="gray.200">
                <Text fontSize="xs" color="gray.600">
                  {(pagination.page - 1) * pagination.limit + 1}-
                  {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total}
                </Text>
                <HStack spacing={1}>
                  <Button
                    size="xs"
                    onClick={() => handlePageChange(pagination.page - 1)}
                    isDisabled={pagination.page === 1}
                  >
                    Prev
                  </Button>
                  {Array.from({ length: Math.min(pagination.pages, 3) }, (_, i) => {
                    const pageNum = i + 1;
                    return (
                      <Button
                        key={pageNum}
                        size="xs"
                        variant={pagination.page === pageNum ? 'solid' : 'outline'}
                        colorScheme={pagination.page === pageNum ? 'blue' : 'gray'}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                  <Button
                    size="xs"
                    onClick={() => handlePageChange(pagination.page + 1)}
                    isDisabled={pagination.page === pagination.pages}
                  >
                    Next
                  </Button>
                </HStack>
              </Flex>
            )}
          </>
        )}
      </Box>

      {/* Generate Invite Modal */}
      <GenerateInviteModal
        isOpen={isGenerateModalOpen}
        onClose={() => setIsGenerateModalOpen(false)}
        onSuccess={handleGenerateSuccess}
      />
    </VStack>
  );
};

export default InvitesPage;
